import { z } from "zod";
import {
  DTODesignSystemCreateInput,
  DTODesignSystemResponse,
  DTODesignSystemsListResponse,
  DTOUserDesignSystemsResponse,
} from "../../dto";
import { DTODesignSystemUpdateAccessModeInput, DTODesignSystemUpdateInput } from "../../payloads";
import { RequestExecutor } from "../../transport/request-executor";
import { DesignSystemBffEndpoint } from "./bff";
import { DesignSystemContactsEndpoint } from "./contact";
import { FigmaNodeStructuresEndpoint } from "./figma-node-structures";
import { DesignSystemMembersEndpoint } from "./members";
import { DesignSystemPageRedirectsEndpoint } from "./redirects";
import { DesignSystemSourcesEndpoint } from "./sources";
import { StorybookEntriesEndpoint } from "./storybook";
import { DesignSystemVersionsEndpoint } from "./versions/versions";
import { DesignSystemAnalyticsEndpoint } from "./analytics";
import { StorybookHostingEndpoint } from "./storybook-hosting";

export class DesignSystemsEndpoint {
  readonly members: DesignSystemMembersEndpoint;
  readonly versions: DesignSystemVersionsEndpoint;
  readonly bff: DesignSystemBffEndpoint;
  readonly sources: DesignSystemSourcesEndpoint;
  readonly storybook: StorybookEntriesEndpoint;
  readonly storybookHosting: StorybookHostingEndpoint;
  readonly contacts: DesignSystemContactsEndpoint;
  readonly redirects: DesignSystemPageRedirectsEndpoint;
  readonly figmaNodeStructures: FigmaNodeStructuresEndpoint;
  readonly analytics: DesignSystemAnalyticsEndpoint;

  constructor(private readonly requestExecutor: RequestExecutor) {
    this.members = new DesignSystemMembersEndpoint(requestExecutor);
    this.versions = new DesignSystemVersionsEndpoint(requestExecutor);
    this.bff = new DesignSystemBffEndpoint(requestExecutor);
    this.sources = new DesignSystemSourcesEndpoint(requestExecutor);
    this.storybook = new StorybookEntriesEndpoint(requestExecutor);
    this.storybookHosting = new StorybookHostingEndpoint(requestExecutor);
    this.contacts = new DesignSystemContactsEndpoint(requestExecutor);
    this.redirects = new DesignSystemPageRedirectsEndpoint(requestExecutor);
    this.figmaNodeStructures = new FigmaNodeStructuresEndpoint(requestExecutor);
    this.analytics = new DesignSystemAnalyticsEndpoint(requestExecutor);
  }

  create(body: DTODesignSystemCreateInput) {
    return this.requestExecutor.json("/design-systems", DTODesignSystemResponse, { method: "POST", body });
  }

  list(wsId: string) {
    return this.requestExecutor.json(`/workspaces/${wsId}/design-systems`, DTODesignSystemsListResponse);
  }

  listUserDesignSystems() {
    return this.requestExecutor.json("/design-systems", DTOUserDesignSystemsResponse);
  }

  get(dsId: string) {
    return this.requestExecutor.json(`/design-systems/${dsId}`, DTODesignSystemResponse);
  }

  delete(dsId: string) {
    return this.requestExecutor.json(`/design-systems/${dsId}`, z.any(), { method: "DELETE" });
  }

  update(dsId: string, body: DTODesignSystemUpdateInput) {
    return this.requestExecutor.json(`/design-systems/${dsId}`, DTODesignSystemResponse, {
      method: "PUT",
      body,
    });
  }

  updateAccessMode(dsId: string, body: DTODesignSystemUpdateAccessModeInput) {
    return this.requestExecutor.json(`/design-systems/${dsId}/access-mode`, DTODesignSystemResponse, {
      method: "PUT",
      body,
    });
  }
}
